import { TableCell, useTheme } from '@mui/material';
import { TableColumn } from '../Table.props';

export interface TableRowCellProps<T> {
  column: TableColumn<T>;
  row: T;
  index: number;
  stickyPosition?: number;
  isStriped?: boolean;
  isSelected?: boolean;
}

/**
 * TableRowCell - Renders individual cells in a table row
 *
 * Features:
 * - Uses column render method for all columns (including selection and actions)
 * - Supports custom cell renderers
 * - Proper styling and alignment
 * - Conditional ellipsis handling for different column types
 * - Fallback to default rendering for columns without render method
 */
export const TableRowCell = <T,>({
  column,
  row,
  index,
  stickyPosition,
  isStriped,
  isSelected,
}: TableRowCellProps<T>) => {
  const theme = useTheme();
  // Determine if this is a special column that shouldn't have ellipsis
  const isSpecialColumn = column.id === '__select' || column.id === '__actions';

  // Determine cell variant based on column type
  const getCellVariant = () => {
    if (column.id === '__select') return 'checkbox';
    if (column.id === '__actions') return 'action';
    return 'data';
  };

  // Calculate background color for sticky cells using solid colors
  const getStickyBackgroundColor = () => {
    if (isSelected) {
      return theme.palette.State.selected; // Solid color #FCFAFF
    }
    if (isStriped) {
      return theme.palette.State.striped; // Solid color #E3E3E4
    }
    return theme.palette.M3.sys.light.surface; // Solid color #FFFFFF
  };

  // Generate sticky styles if column is sticky
  const stickyStyles = column.sticky
    ? {
        position: 'sticky' as const,
        [column.sticky]: `${stickyPosition}px`,
        zIndex: 2, // Higher than row states but lower than headers
        backgroundColor: getStickyBackgroundColor(),
        // Handle hover state with solid colors
        '.MuiTableRow-root:hover &': {
          backgroundColor: isSelected
            ? theme.palette.State['selected-hover'] // Solid color #E9EDFD for hover on selected
            : isStriped
              ? theme.palette.State.striped // Solid color #E3E3E4 for striped hover
              : theme.palette.State.hover, // Solid color #F5F5F5
        },
        '&.MuiTableCell-root': {
          zIndex: 2, // Override any parent row selectors
        },
      }
    : {};

  // Use render method if provided (handles selection, actions, and custom columns)
  if (column.render) {
    const rowRecord = row as Record<string, unknown>;
    const value =
      column.id in rowRecord ? rowRecord[column.id as string] : undefined;
    const cellContent = column.render(value, row, index);

    return (
      <TableCell
        align={column.align}
        variant={getCellVariant()}
        sx={{
          width: column.width,
          minWidth: column.minWidth,
          borderBottomColor: theme.palette.M3.sys.light['outline 2'],
          ...stickyStyles,
          zIndex: column.sticky ? 2 : undefined, // Explicitly set for sticky columns
        }}
        // Add title for text columns to show full text on hover
        title={
          !isSpecialColumn && typeof value === 'string' ? value : undefined
        }
      >
        {cellContent}
      </TableCell>
    );
  }

  // Fallback for columns without render method
  const rowRecord = row as Record<string, unknown>;
  const value =
    column.id in rowRecord ? rowRecord[column.id as string] : undefined;
  const cellContent = String(value || '');

  return (
    <TableCell
      align={column.align}
      variant={getCellVariant()}
      sx={{
        width: column.width,
        minWidth: column.minWidth,
        borderBottomColor: theme.palette.M3.sys.light['outline 2'],
        ...stickyStyles,
        zIndex: column.sticky ? 2 : undefined, // Explicitly set for sticky columns
      }}
      title={cellContent} // Show full text on hover for data cells
    >
      {cellContent}
    </TableCell>
  );
};
